import React, { useState } from 'react';
import ThreediumSolutionPicker from '../ThreediumSolutionPicker';

/**
 * BuilderStartScreen - Initial screen for starting a new build
 * 
 * Options:
 * 1. Use existing Threedium workspace (solution) - creates project with unlimited3d template
 * 2. Create new project from scratch (vite-react template)
 */
function BuilderStartScreen({ onSelectExperience, onSelectTemplate }) {
  const [selectedSolutionId, setSelectedSolutionId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isCreatingR3F, setIsCreatingR3F] = useState(false);
  const [isCreatingFromSolution, setIsCreatingFromSolution] = useState(false);

  // Handle creating a project from a Threedium solution
  const handleUseWorkspace = async () => {
    if (!selectedSolutionId) return;
    
    setIsCreatingFromSolution(true);
    try {
      // Call template handler with unlimited3d template and solution ID
      await onSelectTemplate('unlimited3d', { solutionId: selectedSolutionId });
    } finally {
      setIsCreatingFromSolution(false);
    }
  };

  const handleCreateNewProject = async () => {
    setIsCreating(true);
    try {
      await onSelectTemplate('threejs');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateR3FProject = async () => {
    setIsCreatingR3F(true);
    try {
      await onSelectTemplate('r3f');
    } finally {
      setIsCreatingR3F(false);
    }
  };

  return (
    <div className="builder-start-screen">
      {/* Background Grid Effect */}
      <div className="builder-bg-grid" />

      {/* Main Content Container */}
      <div className="builder-start-container">
        {/* Heading Section */}
        <div className="builder-heading">
          <div className="builder-heading-titles">
            <h1 className="builder-title">
              Build Web Applications
            </h1>
            <h2 className="builder-title-gradient">
              With AI Assistance
            </h2>
          </div>
          <p className="builder-subtitle">
            Use an existing Threedium workspace or start a fresh project
          </p>
        </div>

        {/* Cards Section */}
        <div className="builder-cards">
          {/* Use Existing Workspace (Threedium Solution) Card */}
          <div
            className={`builder-card ${selectedSolutionId ? 'selected' : ''}`}
          >
            <div className="builder-card-content">
              <div className="builder-card-icon">
                <WorkspaceIcon />
              </div>
              <h3 className="builder-card-title">
                Use existing<br />workspace
              </h3>
              <p className="builder-card-description">
                Select a 3D experience from your Threedium account.
              </p>
            </div>

            {/* Threedium Solution Picker */}
            <div className="builder-solution-picker-container">
              <ThreediumSolutionPicker
                value={selectedSolutionId}
                onChange={setSelectedSolutionId}
                placeholder="Search your Threedium solutions..."
                disabled={isCreatingFromSolution}
              />
            </div>

            {/* Use Workspace Button */}
            {selectedSolutionId && (
              <button
                className="builder-use-btn"
                onClick={handleUseWorkspace}
                disabled={isCreatingFromSolution}
              >
                {isCreatingFromSolution ? 'Creating...' : 'Use This Workspace'}
              </button>
            )}
          </div>

          {/* Create New Three.js Project Card */}
          <div className="builder-card">
            <div className="builder-card-content">
              <div className="builder-card-icon">
                <TemplateIcon />
              </div>
              <h3 className="builder-card-title">
                Three.js<br />project
              </h3>
              <p className="builder-card-description">
                Vanilla Three.js game project with a single AI agent.
              </p>
            </div>
            <button
              className="builder-template-btn"
              onClick={handleCreateNewProject}
              disabled={isCreating}
            >
              {isCreating ? 'Creating...' : 'Create Three.js Project'}
            </button>
          </div>

          {/* Create R3F Project Card */}
          <div className="builder-card">
            <div className="builder-card-content">
              <div className="builder-card-icon">
                <R3FIcon />
              </div>
              <h3 className="builder-card-title">
                React Three<br />Fiber project
              </h3>
              <p className="builder-card-description">
                Declarative 3D with R3F, Drei, and Zustand. Multi-agent team.
              </p>
            </div>
            <button
              className="builder-template-btn"
              onClick={handleCreateR3FProject}
              disabled={isCreatingR3F}
            >
              {isCreatingR3F ? 'Creating...' : 'Create R3F Project'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icon Components
function WorkspaceIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1L14 4.5V11.5L8 15L2 11.5V4.5L8 1Z" stroke="#60D2CC" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M8 15V8" stroke="#60D2CC" strokeWidth="1.2" />
      <path d="M2 4.5L8 8L14 4.5" stroke="#60D2CC" strokeWidth="1.2" />
    </svg>
  );
}

function TemplateIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 2L2 5.5V10.5L8 14L14 10.5V5.5L8 2Z" stroke="#60D2CC" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M8 14V8" stroke="#60D2CC" strokeWidth="1.2" />
      <path d="M2 5.5L8 9L14 5.5" stroke="#60D2CC" strokeWidth="1.2" />
      <circle cx="8" cy="5.5" r="1" fill="#60D2CC" />
    </svg>
  );
}

function R3FIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1L14 4.5V11.5L8 15L2 11.5V4.5L8 1Z" stroke="#60D2CC" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M8 15V8" stroke="#60D2CC" strokeWidth="1.2" />
      <path d="M2 4.5L8 8L14 4.5" stroke="#60D2CC" strokeWidth="1.2" />
      <circle cx="5" cy="6.5" r="0.8" fill="#60D2CC" />
      <circle cx="11" cy="6.5" r="0.8" fill="#60D2CC" />
      <circle cx="8" cy="11" r="0.8" fill="#60D2CC" />
    </svg>
  );
}

export default BuilderStartScreen;
